"use client"

import type React from "react"
import {useEffect} from "react"
import Link from "next/link"
import Image from "next/image"
import {Button} from "@/components/ui/button"
import {Input} from "@/components/ui/input"
import {Facebook, Globe, Instagram, Linkedin, Twitter} from "lucide-react"
import {type Language, useI18n} from "@/lib/i18n"

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const { t, language, setLanguage, loadTranslations } = useI18n()

  // Load footer translations
  useEffect(() => {
    loadTranslations("footer")
  }, [loadTranslations])

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLanguage(e.target.value as Language)
  }

  return (
    <footer className="bg-gray-900 text-gray-300 pt-14 sm:pt-18 pb-8 sm:pb-10">
      <div className="container mx-auto px-4 sm:px-6 md:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 mb-10 sm:mb-14">
          <div className="sm:col-span-2 lg:col-span-1">
            <Link href="/" className="flex items-center space-x-3 mb-5 sm:mb-6 group">
              <div className="relative h-8 w-8 sm:h-10 sm:w-10">
                <Image
                  src="/logo.png"
                  alt="MyJobAI.de Logo"
                  width={40}
                  height={40}
                  className="object-contain brightness-0 invert dark:brightness-0 dark:invert transition-transform duration-200 group-hover:scale-105"
                />
              </div>
              <span className="text-xl sm:text-2xl font-bold text-white">MyJobAI.de</span>
            </Link>
            <p className="text-gray-400 mb-5 sm:mb-6 text-sm sm:text-base leading-relaxed">{t("footer_tagline")}</p>
            <div className="flex gap-3 sm:gap-4">
              <Link
                href="#"
                className="text-gray-400 hover:text-white transition-all duration-200 hover:scale-110 p-2 rounded-lg hover:bg-gray-800"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5 sm:h-6 sm:w-6" />
              </Link>
              <Link
                href="#"
                className="text-gray-400 hover:text-white transition-all duration-200 hover:scale-110 p-2 rounded-lg hover:bg-gray-800"
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5 sm:h-6 sm:w-6" />
              </Link>
              <Link
                href="#"
                className="text-gray-400 hover:text-white transition-all duration-200 hover:scale-110 p-2 rounded-lg hover:bg-gray-800"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5 sm:h-6 sm:w-6" />
              </Link>
              <Link
                href="#"
                className="text-gray-400 hover:text-white transition-all duration-200 hover:scale-110 p-2 rounded-lg hover:bg-gray-800"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5 sm:h-6 sm:w-6" />
              </Link>
            </div>
          </div>

          <div>
            <h3 className="text-white font-bold mb-5 sm:mb-6 text-base sm:text-lg">{t("quick_links")}</h3>
            <ul className="space-y-3 sm:space-y-3.5">
              <li>
                <Link href="#" className="text-gray-400 hover:text-white transition-all duration-200 text-sm sm:text-base hover:translate-x-1 inline-block">
                  {t("home")}
                </Link>
              </li>
              <li>
                <Link href="#how-it-works" className="text-gray-400 hover:text-white transition-all duration-200 text-sm sm:text-base hover:translate-x-1 inline-block">
                  {t("how_it_works")}
                </Link>
              </li>
              <li>
                <Link href="#pricing" className="text-gray-400 hover:text-white transition-all duration-200 text-sm sm:text-base hover:translate-x-1 inline-block">
                  {t("pricing")}
                </Link>
              </li>
              <li>
                <Link href="#faq" className="text-gray-400 hover:text-white transition-all duration-200 text-sm sm:text-base hover:translate-x-1 inline-block">
                  {t("faq")}
                </Link>
              </li>
              <li>
                <Link href="/blog" className="text-gray-400 hover:text-white transition-all duration-200 text-sm sm:text-base hover:translate-x-1 inline-block">
                  {t("blog")}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-bold mb-5 sm:mb-6 text-base sm:text-lg">{t("legal")}</h3>
            <ul className="space-y-3 sm:space-y-3.5">
              <li>
                <Link href="/privacy" className="text-gray-400 hover:text-white transition-all duration-200 text-sm sm:text-base hover:translate-x-1 inline-block">
                  {t("privacy_policy")}
                </Link>
              </li>
              <li>
                <Link href="/terms" className="text-gray-400 hover:text-white transition-all duration-200 text-sm sm:text-base hover:translate-x-1 inline-block">
                  {t("terms_of_use")}
                </Link>
              </li>
              <li>
                <Link href="/cookies" className="text-gray-400 hover:text-white transition-all duration-200 text-sm sm:text-base hover:translate-x-1 inline-block">
                  {t("cookie_policy")}
                </Link>
              </li>
              <li>
                <Link href="/gdpr" className="text-gray-400 hover:text-white transition-all duration-200 text-sm sm:text-base hover:translate-x-1 inline-block">
                  {t("gdpr_compliance")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="sm:col-span-2 lg:col-span-1">
            <h3 className="text-white font-bold mb-5 sm:mb-6 text-base sm:text-lg">{t("newsletter")}</h3>
            <p className="text-gray-400 mb-4 sm:mb-5 text-sm sm:text-base leading-relaxed">{t("newsletter_description")}</p>
            <div className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-7">
              <Input
                type="email"
                placeholder={t("your_email")}
                className="bg-gray-800 border-gray-700 text-white text-sm sm:text-base focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-200"
                aria-label="Email address"
              />
              <Button className="bg-primary hover:bg-primary/90 text-white text-sm sm:text-base shadow-sm hover:shadow-md transition-all duration-200">
                {t("subscribe")}
              </Button>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center space-x-2 mb-3">
                <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-primary" aria-hidden="true" />
                <span className="text-white font-semibold text-sm sm:text-base">{t("language")}</span>
              </div>
              <select
                className="bg-gray-700 border border-gray-600 rounded-lg text-white px-3 sm:px-4 py-2.5 w-full text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                value={language}
                onChange={handleLanguageChange}
                aria-label="Select language"
              >
                <option value="de">Deutsch</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="fa">فارسی</option>
              </select>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-6 sm:pt-8">
          <p className="text-center text-gray-500 text-xs sm:text-sm leading-relaxed">
            © {currentYear} MyJobAI.de. {t("all_rights_reserved")} |{" "}
            <Link href="/impressum" className="hover:text-gray-300 transition-colors duration-200 underline decoration-gray-600 hover:decoration-gray-400 underline-offset-2">
              {t("imprint")}
            </Link>
          </p>
        </div>
      </div>
    </footer>
  )
}
